import { ReactElement, useEffect } from 'react';

import { NextPageWithLayout } from '../components/layouts';
import LandingPageLayout from '../components/layouts/LandingPageLayout';
import useAccessBadge from '../hooks/useAccessBadge';
import useAttioEventTracking from '../hooks/useAttioEventTracking';

const meta = {
  author: 'Eirik Herness Berre',
};

const TermsOfUsePage: NextPageWithLayout = () => {
  const { accessBadge } = useAccessBadge();
  const trackAttioEvent = useAttioEventTracking();

  useEffect(() => {
    const userRole = accessBadge?.user?.profileInfo?.role;
    const organizationId = accessBadge?.organization?.id;
    const email = accessBadge?.user?.email;
    const userId = accessBadge?.user?.id;

    if (userRole && organizationId && userId && email) {
      // Paused until decision on product event
      // trackAttioEvent('terms_of_use_page_view', {
      //   organizationId,
      //   userRole,
      //   userId,
      //   email,
      //   firstName: accessBadge?.user?.firstName,
      //   lastName: accessBadge?.user?.lastName,
      // });
    }
  }, [accessBadge, trackAttioEvent]);

  return (
    <div>
      <h1>
        <strong>Terms of Use for Curipod</strong>
      </h1>

      <p>Last update: December 16th, 2024</p>

      <h2>
        <strong>PLEASE READ THIS DOCUMENT CAREFULLY</strong>
      </h2>

      <h3>1. Introduction and Definitions</h3>

      <p>First of all, thank you for using Curipod 😊</p>

      <p>
        1.1 This document sets out the terms for use of the Curipod software and website
        (from now refered to as the &quot;<strong>Services</strong>&quot; or &quot;
        <strong>Curipod Platform</strong>&quot;), . It governs how Curipod (sometimes
        referred to as &quot;<strong>us</strong>&quot;, &quot;<strong>we</strong>&quot; or
        &quot;<strong>our</strong>&quot;) provide our Services to Customers and Users.
        When we talk about Customers and Users we might also refer to &quot;
        <strong>You</strong>&quot; or &quot;<strong>Your</strong>&quot;.{' '}
      </p>

      <p>
        1.2 Curipod is committed to transparency, which includes providing a Terms of Use
        that is understandable and written in plain language. Because this document
        represents our agreement with You about your use of our Services, please take the
        time to read this document.
      </p>

      <p>
        1.3 Our Terms of Use (&quot;<strong>Terms</strong>&quot; or &quot;
        <strong>Agreement</strong>&quot;) also includes our Privacy Policy and Cookie
        Policy available at https://curipod.com, and any other documents referred to by
        those agreements, and they, as a whole, govern any and all of the access to the
        Services.
      </p>

      <p>
        1.4 By accepting the Terms when purchasing and/or subscribing for the Services you
        receive a non-exclusive, non-transferable, revocable and limited in time right to
        use the Services with the functionality and limits included in your chosen Plan,
        hereinafter defined as a &quot;<strong>License</strong>&quot;.
      </p>

      <p>
        1.5 By using our Platform in any manner You are expressly agreeing to, and give
        your consent to be governed by, these Terms. If you do not agree with these Terms
        or You do not wish to be bound by these Terms, you must not use or access our
        Services in any manner.
      </p>

      <p>
        &quot;<strong>Account</strong>&quot; means the account you create using an email
        to access the Service
      </p>

      <p>
        &quot;<strong>Agreement</strong>&quot; - Refers to this agreement.
      </p>

      <p>
        &quot;<strong>User</strong>&quot; is a person who is using the Services, either by
        creating an Account or participating as part of an Audience.
      </p>

      <p>
        &quot;<strong>You</strong>&quot; - We refer to you as a User.{' '}
      </p>

      <p>
        &quot;<strong>Organization</strong>&quot; means a school, district, business or
        other organization purchasing Licenses.
      </p>

      <p>
        &quot;<strong>Presentation</strong>&quot; means the presentations, lessons,
        activities or slides created with the Services{' '}
      </p>

      <p>
        &quot;<strong>Plan</strong>&quot; means the version of the Services you pay for.
        Different versions included different features or limits described here:{' '}
        <a href="https://curipod.com/pricing">https://curipod.com/pricing</a>
      </p>

      <p>
        &quot;<strong>Free Plan</strong>&quot; means the Plan where You use the Services
        without paying for the Service, and where you are not part of an School & District
        Plan.
      </p>

      <p>
        &quot;<strong>Individual Subscription</strong>&quot; means the Plan where You pay
        directly through Curipod for yourself or Your team
      </p>

      <p>
        &quot;<strong>School & District Plan</strong>&quot; means a Plan where an
        Organization have a seperate license agreement and/or data processing agreement
        with us.
      </p>

      <p>
        &quot;<strong>License</strong>&quot; - means the license granted to you as
        described in section 1.4 in this Agreement.
      </p>

      <p>
        &quot;<strong>Audience</strong>&quot; means the individuals for which a
        Presentation is created and who are the respondents during a Presentation.{' '}
      </p>

      <p>
        &quot;<strong>Services</strong>&quot; - means all services provided by Curipod.{' '}
      </p>

      <p>
        &quot;<strong>Curipod</strong>&quot; is the company Curipod Inc for customers
        located in the US, and the company Curipod AS for other users
      </p>

      <p>
        &quot;<strong>Curipod Discover</strong>&quot; is a part of the Services where
        Presentations are shared publicly.{' '}
      </p>

      <h3>
        <strong>2. Age of Access</strong>
      </h3>

      <p>
        2.1. If you are under the age of 18 you may access the service by entering the PIN
        code provided by your teacher.
      </p>

      <p>
        2.2. If you are a student above the age of 18 you may create your own User Account
        in accordance with the instructions of your school or district. Please review our
        Privacy Policy to learn how Curipod process your personal information.
      </p>

      <p>
        2.3. Other age specific restrictions of access might be imposed from the school or
        district using the Service.
      </p>

      <h3>
        <strong>3. Code of Conduct</strong>
      </h3>

      <p>
        3.1 We have to set up some ground rules. Honestly, most of what we cover here
        should go without saying. However, it&apos;s better to be upfront about things,
        rather than just assume everyone knows how they are expected to conduct themselves
        using our Services. These rules are all referred to as our &quot;
        <strong>Code of Conduct.</strong>&quot;
      </p>

      <ul>
        <li>
          No Illegal Activity: This is about as simple as it gets. Do not use the Services
          for any illegal activity. Period. You are not allowed to use our Services to
          engage in any kind of conduct that violates any applicable federal, state,
          local, or international law or regulation (including, without limitation, any
          laws regarding the export of data or software to and from the US or other
          countries)
        </li>
        <li>
          No Fraud: Yes, this is probably covered in the No Illegal Activity section
          above, but we want to make this very clear. Fraud will not be tolerated in any
          capacity
        </li>
        <li>
          No Bad Code: Do not use the Services to transmit, distribute, send, etc.
          viruses, worms, or any other code that may be deemed dangerous, harmful, or
          destructive in any manner
        </li>
        <li>
          No Spamming: Seriously. No one likes spam. We don&apos;t like spam. We&apos;re
          pretty sure that you don&apos;t like your inbox cluttered with spam. You may not
          use the Services to engage in any activities that will result in sending spam to
          anyone using the Services.
        </li>
        <li>
          No Exploitation: You will not use the Services to try to gather personal
          information on anyone.
        </li>
        <li>
          No Impersonation of Curipod or its employees: Imitation is the sincerest form of
          flattery, but not when it comes to representing that You are a part of Curipod.
          You will not impersonate Curipod or any of its employees
        </li>
        <li>
          No Data Mining or Bots: You may not use any data mining, robots, or similar data
          gathering or extraction methods; and
        </li>
        <li>
          No Use Other Than Intended: You may not use the Services or any content
          contained on the Platform for any purposes other than intended
        </li>
        <li>
          If You violate this Code of Conduct we reserve the right to remove You from the
          Services. Whether conduct violates our Code of Conduct will be determined in
          Curipod&apos;s sole discretion.
        </li>
      </ul>

      <h3>
        <strong>5. Course and Services Content</strong>
      </h3>

      <p>
        5.1 While we&apos;re on the subject of ground rules, we should also discuss what
        content is prohibited on the Curipod Platform. For a number of reasons, there are
        certain subject matter types that we simply will not allow to be published on the
        Curipod Platform.
      </p>

      <p>
        5.2 Curipod reserves the right to remove You and your content should your content
        contain material (determined, in our sole discretion) that is libelous,
        slanderous, pornographic, obscene, unlawful, threatening, defamatory, or otherwise
        objectionable or violates any party&apos;s intellectual property or any other part
        of this Agreement
      </p>

      <h3>
        <strong>6. Intellectual Property and Your content</strong>
      </h3>

      <p>
        In operating our Platform, it&apos;s important for us to make it clear who owns
        what. You are trusting us with your content and consistent with Curipod&apos;s
        genuine belief in transparency we want to make it simple for You to know exactly
        what You are getting into when You join the Curipod community.
      </p>

      <p>
        6.1 Curipod content: Content on the Platform, including all information, software,
        technology, data, logos, marks, designs, text, graphics, pictures, audio and video
        files, other data or copyrightable materials or content, and their selection and
        arrangement, is referred to herein as &quot;<strong>Curipod Content</strong>
        &quot;, and is and remains the sole property of Curipod. Curipod Content,
        including our trademarks, may not be modified by You in any way.
      </p>

      <p>
        6.2 &quot;<strong>Your Content</strong>&quot; is content that You upload to, or
        create using our Services (e.g. Presentations, lessons, customizable lessons,
        AI-prompts etc.). Your Content is considered &quot;
        <strong>Public User Content</strong>&quot; unless you actively opt out, or you are
        part of an School & District Plan<strong>.</strong> If you wish to make content
        only visible to you, &quot;<strong>Private Content</strong>
        &quot;, you must actively change lesson settings, or the default settting in your
        profile page. We want to build a community where sharing is the norm, but where
        you still have the option of creating Private Content.{' '}
      </p>

      <ul>
        <li>
          You retain ownership of all rights, including intellectual property rights by
          uploading your content to our Services for both Public User Content and Private
          Content.
        </li>
        <li>
          Curipod may review your Public User Content for any reason, including compliance
          with our Code of Conduct and prohibited content requirements, but Curipod has no
          obligation to review anything that You upload.
        </li>
        <li>
          By making Public User Content, you represent and warrant that the downloading,
          copying and use of the Public User Content will not infringe the proprietary
          rights, including but not limited to the copyright, patent, trademark or trade
          secret rights, of any third party;
        </li>
        <li>
          For the School & District Plan, the default setting for content made by the
          users in your organization is set to Private Content.
        </li>
        <li>
          By making Public User Content, you hereby grant to Curipod a perpetual (or, for
          as long as permitted under applicable law), non-exclusive, sub-licensable,
          transferable, royalty-free, irrevocable, fully paid, universal license to
          commercialize, use, reproduce, make available to the public (e.g. perform or
          display), publish, translate, modify, create derivative works from, and
          distribute your Public User Content through any medium, whether alone or in
          combination with other content or materials, in any manner and by any means,
          method or technology, whether now known or hereafter created.
        </li>
        <li>
          Curipod needs this license because you own your content and Curipod therefore
          can&apos;t display it across its various surfaces (i.e., mobile, web) without
          your permission.
        </li>
        <li>
          This type of license also is needed to distribute your content across our
          Services. For example, if we want to showcase Public User Content on Curipod
          Discover. It is reproduced as versions on both our website and app, and
          distributed to multiple places within Curipod, such as the page where users can
          browse Curipod Discover. A modification might be that we show a snippet of your
          work (and not the full Presentation) in a preview, with attribution to you.
        </li>
        <li>
          We will of course remove your personal data related to Public User Content on
          request.
        </li>
        <li>
          &quot;<strong>Your Results</strong>&quot; are the text, images, videos, drawings
          made by participants during a Curipod lesson you host. Curipod will never
          publish Your Results.
        </li>
      </ul>

      <p>
        6.3 As we asks others to respect our intellectual property rights, we respects the
        intellectual property rights of others. If you believe that material located on or
        linked to by Curipod violates your copyright, you are encouraged to notify Curipod
        by email at eirik@curipod.com. Curipod will respond to all such notices, including
        as required or appropriate by removing the infringing material or disabling all
        links to the infringing material. In the case of a User who may infringe or
        repeatedly infringes the copyrights or other intellectual property rights of
        Curipod or others, Curipod may, in its discretion, terminate or deny access to and
        use of the Services.{' '}
      </p>

      <h3>
        <strong>7. Curipod&apos;s General Rights In Operating Its Platform</strong>
      </h3>

      <p>
        7.1 Curipod reserves the following rights over the entire Curipod Platform:
        Curipod may modify, terminate, or refuse to provide Curipod Services at any time
        due to business considerations or other reasons, without notice. Curipod may
        remove anyone from the Curipod Platform at any time for any reason, solely in
        Curipod&apos;s discretion. This right is not modified by any other section of this
        Agreement.
      </p>

      <h3>
        <strong>8. Third Party Communications</strong>
      </h3>

      <p>
        8.1 By using Curipod&apos;s Platform, You may see Public User Content from from
        third parties (e.g. a teacher publish content to Curipod Discover). Curipod is not
        responsible for this content and shall not have any liability in connection with
        them. You are solely responsible for assessing and verifying the identity and
        trustworthiness of the source and content of any such third-party content. Curipod
        assumes no responsibility for verifying, and makes no representations or
        warranties regarding the identity or trustworthiness of the source or content of
        any such third-party content.
      </p>

      <h3>
        <strong>9. Charges and Payment</strong>
      </h3>

      <p>
        9.1 The pricing plans available for the Service are available at the pricing page
        of curipod.com. All prices are exclusive of VAT, other taxes and potential tolls
        or import/export fees. We reserve the right to change the pricing and or the
        offering of each plan at our sole discretion. Your rights and obligations with
        respect to certain part of our Services will be based in part on the pricing plan
        you choose.{' '}
      </p>

      <p>
        9.2 You can buy a School & District Plan or an Individual Subscription. What each
        plan includes is found on our website.
      </p>

      <p>
        9.3 To purchase a School & District Plan, you can submit a purchase order. By
        submitting a purchase order, You agree to pay the fees owed in full within thirty
        (30) days of receiving an invoice from Us, unless otherwise specified on a quote
        you have received from us. You represent and warrant that you have the legal right
        to use all payment method(s) you provide to us. YOU HEREBY ACKNOWLEDGE AND AGREE
        THAT ANY AND ALL PURCHASE ORDERS SUBMITTED BY YOU TO US AND FEES PAID BY YOU TO US
        IN CONNECTION WITH THE SERVICES ARE FINAL, NON-REFUNDABLE, AND NON-CANCELLABLE.
      </p>

      <p>
        9.4 When you purchase an Individual Subscription, you expressly authorize us or
        our third-party payment processor to charge you for such Services. You represent
        and warrant that you have the legal right to use all payment methods that you
        provide to us.
      </p>

      <p>
        9.5 Individual Subscription involve recurring fees (each, along with any
        applicable taxes and other charges are a &quot;Subscription Fee&quot;). Depending
        on which options you choose, those fees may recur each month, or year thereafter,
        at the then-current rate. Please note that our fees are subject to change,
        although we will notify you before we effect any change.
      </p>

      <p>
        9.6 You may cancel your Individual Subscription at any time but please note that
        such cancellation will only be effective at the end of the then-current
        Subscription period. Unless required by law, you will not receive a refund of any
        portion of the subscription fee paid for the then-current subscription periond at
        the time of cancellation.
      </p>

      <p>
        9.7 Subscription Fees do not include taxes, and you agree to: (a) pay all
        sales/use, gross receipts, value-added, GST, personal property, or other tax
        (including any interest and penalties) with respect to the transactions and
        payments under these Terms, other than taxes based on our income, employees, or
        real property; and (b) be responsible for any filing of any information or tax
        returns with respect thereto.
      </p>

      <p>
        9.8 In the event that you fail to pay the full amount owed to us, we may limit
        your access to the Services, in addition to any other rights or remedies we may
        have.
      </p>

      <p>
        9.9 Accounts are individual, meaning that one Account can only be used by one
        person and you agree not to permit any other person to your License.
      </p>

      <h3>
        <strong>10. Limitations of Liability</strong>
      </h3>

      <p>
        In running the Platform, we require that You understand and agree that Curipod is
        not liable for a number of things, including Your breach of this Agreement and
        losses that may result from Your use of the Platform. We also require that You
        understand and agree that the Platform is offered As Is and may not always be 100%
        perfect or reliable and that Curipod doesn&apos;t warrant or guarantee that it
        will always be free of errors or defects. Any claims for damages against Curipod
        will be limited to a portion of the fees You have paid us. Our limitations of
        liability are as follows:
      </p>

      <p>
        10.1 You agree that Curipod, including its employees, officers, directors, and
        agents, shall not be liable for any kind of loss, injury, claim, or damages
        resulting from Your use of the Curipod Platform
      </p>

      <p>
        10.2 You agree that Curipod shall not be liable for any injuries, losses, claims,
        or damages that result from Your use or engagement with third party content,
        downloads, or communications
      </p>

      <p>
        10.3 Curipod is not liable for any losses or damage caused by any viruses,
        distributed denial of service attacks, and any and all other malicious computer
        code. Malicious computer code means computer code or other mechanisms of any kind
        designed to disrupt, disable or harm in any manner the operation of any software
        or hardware or other business processes or to misuse, gain unauthorized access to
        or misappropriate any business or personal information, including worms, bombs,
        backdoors, clocks, timers, or other disabling device code, or designs or routines
        that cause software or information to be erased, inoperable, or otherwise
        incapable of being used, either automatically or with passage of time or upon
        command
      </p>

      <p>
        10.4 Curipod does not guarantee that any files available for downloading from the
        Platform will be completely free from viruses or other harmful computer code
      </p>

      <p>
        10.5 The Curipod Platform is provided to You on an &quot;As Is, As Available&quot;
        basis. As such, You agree to have an external method of recovering any lost data
        that may be uploaded to the Curipod Platform
      </p>

      <p>
        10.6 Curipod disclaims any warranties to the Platform and the content contained
        therein, including warranties for merchantability, fitness for a particular
        purpose, non-infringement, or title.
      </p>

      <p>
        10.7 Curipod shall not be liable for any special, direct, indirect, incidental,
        punitive, or consequential damages, resulting from the use of or inability to use
        the Platform
      </p>

      <p>
        10.8 You agree that Curipod shall not be liable for any losses, injuries, claims,
        or damages from Your violation or breach of this Agreement. You agree to
        indemnify, defend, and hold harmless Curipod , its present and future officers,
        directors, employees, agents, licensors, suppliers, and any third party
        information providers to the Platform from and against all claims, losses,
        expenses, damages, and costs, including reasonable attorneys&apos; fees, resulting
        from any violation of this Agreement by You or arising from or related to Your use
        or misuse of the Platform (including, without limitation, use in contravention of
        this Agreement, other Curipod policies, and copyright and other intellectual
        property law)
      </p>

      <p>
        10.9 You are solely responsible for ensuring that Your use of the Platform
        complies with applicable law and does not violate the rights of any third party,
        including, without limitation, intellectual property rights. You assume all
        liability for any claims, suits or grievances filed against You, including, but
        not limited to, all damages related to Your use of the Platform.
      </p>

      <p>
        10.10. You may not assign Your rights under this Agreement without Curipod&apos;s
        prior written consent
      </p>

      <p>
        10.11 Should Curipod&apos;s limitation of liability not be applicable,
        Curipod&apos;s sole obligation to You, or any third party, for damages shall not
        exceed the amounts paid by You to Curipod over the prior twelve (12) months
        directly preceding your claim, unless otherwise required by law
      </p>

      <p>
        10.12 You agree that Curipod shall not be liable for any Public User Content that
        appears on the Platform
      </p>

      <p>
        10.13. Except as set forth herein, nothing express or implied in this Agreement is
        intended or implied to confer, and nothing herein shall confer, any rights,
        remedies, liabilities, or obligations whatsoever upon any person or entity
      </p>

      <p>
        10.14. You agree that any cause of action or claim that You may have against
        Curipod must be brought against us within one (1) year after the cause of action
        or claim arises. Should You not commence such cause of action or claim within this
        time period, it shall be barred
      </p>

      <h3>
        <strong>11. Changes to the Terms of Use</strong>
      </h3>

      <p>
        11.1 We may review and update this Terms of Use from time to time in our sole
        discretion. As such, your continued use of the Services after receipt of
        notification of changes to this Terms of Use is deemed to be acceptance of those
        changes. The notification will be provided in reasonable time before changes
        apply.
      </p>

      <h3>12. Disputes and governing laws</h3>

      <p>
        The Agreement is governed by the laws of Norway without regard to the conflict of
        laws and the United Nations Convention on the International Sales of Goods.
        Norwegian law will apply to all matters relating to the use of our Services and
        the purchase of any products or services through the Platform.
      </p>

      <p>
        Any action to enforce the Agreement shall be brought in the courts located in Oslo
        (Oslo tingrett), Norway. You hereby agree to the jurisdiction by such courts, and
        waive any jurisdictional, venue, or inconvenient forum objections to such courts.
      </p>

      <h3>
        <strong>13. Titles/Headings</strong>
      </h3>

      <p>
        13.1 The headings and titles of sections to this Agreement have been inserted for
        the convenience of reference for the parties and are for informational purposes
        only and shall not be deemed to be a part of the language of this Agreement.
        Neither You nor Curipod shall rely on, or interpret substantively, the headings
        when construing the meaning of each section or the Agreement as a whole.
      </p>

      <h3>
        <strong>14. Gender/Plural</strong>
      </h3>

      <p>
        14.1 Whenever such wording may appear in this Agreement, words in the singular
        shall mean and include the plural and vice versa and words in the feminine shall
        mean and include the masculine and vice versa.
      </p>

      <p>
        <strong>
          Curipod AS – Org.Nr : 923 641 645
          <br />
          Email: eirik@curipod.com
        </strong>
      </p>
    </div>
  );
};

TermsOfUsePage.getLayout = function getLayout(page: ReactElement) {
  return (
    <LandingPageLayout
      disableHeadline={true}
      mdx={true}
      hideActions={true}
      title="Terms of use"
      subTitle="Terms of Use for Curipod"
    >
      {page}
    </LandingPageLayout>
  );
};

export default TermsOfUsePage;
